import type { LoaderFunctionArgs, MetaFunction } from "@remix-run/node";
import { json } from "@remix-run/node";
import { useLoaderData } from "@remix-run/react";
import { detectLocale, localePath } from "~/utils/locale";
import type { Locale } from "~/utils/locale";
import { buildPageMeta, originFromRequest } from "~/utils/storefrontMeta";

export const loader = async ({ request }: LoaderFunctionArgs) => {
  return json(
    { locale: detectLocale(request.url), origin: originFromRequest(request) },
    {
      headers: {
        "Cache-Control": "public, max-age=3600, s-maxage=86400",
      },
    },
  );
};

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const locale = (data?.locale || "vi") as Locale;
  const origin = data?.origin || "https://diolo.com";
  return buildPageMeta({
    title:
      locale === "vi"
        ? "Khả năng tiếp cận — Diolo"
        : "Accessibility — Diolo",
    description:
      locale === "vi"
        ? "Diolo cam kết tạo trải nghiệm mua sắm dễ tiếp cận cho mọi khách hàng."
        : "Diolo is committed to making our store accessible for everyone.",
    path: localePath("/accessibility", locale),
    origin,
    locale,
  });
};

export default function AccessibilityPage() {
  const { locale } = useLoaderData<typeof loader>() as { locale: Locale };
  return (
    <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6 lg:px-8">
      <h1 className="text-2xl font-normal text-black tracking-[0.02em] mb-6">
        {locale === "vi" ? "Tuyên bố khả năng tiếp cận" : "Accessibility Statement"}
      </h1>

      <p className="text-sm text-[#555] leading-relaxed mb-8">
        {locale === "vi"
          ? "Diolo cam kết mang đến trải nghiệm mua sắm dễ tiếp cận cho tất cả khách hàng, bao gồm những người sử dụng công nghệ hỗ trợ. Chúng tôi luôn nỗ lực cải thiện trang web theo các tiêu chuẩn tiếp cận quốc tế."
          : "Diolo is committed to providing an accessible shopping experience for every customer, including those who rely on assistive technologies. We continuously work to align our website with international accessibility standards."}
      </p>

      <h2 className="text-base font-normal uppercase tracking-[0.1em] text-black mt-10 mb-3">
        {locale === "vi" ? "Tiêu chuẩn áp dụng" : "Standards we follow"}
      </h2>
      <p className="text-sm text-[#555] leading-relaxed mb-3">
        {locale === "vi"
          ? "Trang web của chúng tôi được thiết kế hướng đến mục tiêu đáp ứng các yêu cầu của Web Content Accessibility Guidelines (WCAG) phiên bản 2.1 cấp AA — bộ tiêu chuẩn được công nhận rộng rãi cho khả năng tiếp cận web."
          : "Our website aims to meet the requirements of the Web Content Accessibility Guidelines (WCAG) version 2.1, level AA — a widely recognized standard for web accessibility."}
      </p>

      <h2 className="text-base font-normal uppercase tracking-[0.1em] text-black mt-10 mb-3">
        {locale === "vi" ? "Các tính năng tiếp cận" : "Accessibility features"}
      </h2>
      <ul className="list-disc list-outside pl-5 text-sm text-[#555] leading-relaxed space-y-1.5">
        <li>
          {locale === "vi"
            ? "Liên kết \"Bỏ qua đến nội dung chính\" cho phép người dùng bàn phím vượt qua thanh điều hướng."
            : "“Skip to main content” link lets keyboard users bypass the navigation."}
        </li>
        <li>
          {locale === "vi"
            ? "Cấu trúc tiêu đề ngữ nghĩa (h1 / h2 / h3) giúp trình đọc màn hình hiểu được bố cục trang."
            : "Semantic heading hierarchy (h1 / h2 / h3) so screen readers can understand page structure."}
        </li>
        <li>
          {locale === "vi"
            ? "Ảnh sản phẩm có văn bản thay thế mô tả nội dung."
            : "Product images carry descriptive alt text."}
        </li>
        <li>
          {locale === "vi"
            ? "Form có nhãn rõ ràng, thông báo lỗi và hỗ trợ điều hướng bằng bàn phím."
            : "Forms have clear labels, error messages, and full keyboard navigation."}
        </li>
        <li>
          {locale === "vi"
            ? "Độ tương phản màu chữ và nền đáp ứng tỷ lệ tối thiểu 4.5:1."
            : "Text and background contrast ratios meet at least 4.5:1."}
        </li>
        <li>
          {locale === "vi"
            ? "Hỗ trợ chuyển đổi ngôn ngữ giữa tiếng Việt và tiếng Anh."
            : "Bilingual support — switch between Vietnamese and English at any time."}
        </li>
      </ul>

      <h2 className="text-base font-normal uppercase tracking-[0.1em] text-black mt-10 mb-3">
        {locale === "vi" ? "Trình duyệt và công nghệ hỗ trợ" : "Browsers and assistive technology"}
      </h2>
      <p className="text-sm text-[#555] leading-relaxed mb-3">
        {locale === "vi"
          ? "Chúng tôi kiểm tra trang web với các trình duyệt phổ biến (Chrome, Safari, Firefox) và các trình đọc màn hình thông dụng (VoiceOver, NVDA, TalkBack). Nếu bạn gặp khó khăn khi sử dụng phần mềm hoặc thiết bị hỗ trợ, vui lòng liên hệ với chúng tôi."
          : "We test on common browsers (Chrome, Safari, Firefox) and assistive technologies (VoiceOver, NVDA, TalkBack). If you encounter any difficulty using assistive software or devices, please reach out."}
      </p>

      <h2 className="text-base font-normal uppercase tracking-[0.1em] text-black mt-10 mb-3">
        {locale === "vi" ? "Phản hồi" : "Feedback"}
      </h2>
      <p className="text-sm text-[#555] leading-relaxed mb-3">
        {locale === "vi"
          ? "Chúng tôi luôn lắng nghe phản hồi để cải thiện trải nghiệm. Nếu bạn gặp rào cản nào về khả năng tiếp cận trên trang, vui lòng email hello@diolo.com với mô tả ngắn gọn về vấn đề bạn gặp phải."
          : "We welcome feedback to make our store better. If you experience any accessibility barrier, email hello@diolo.com with a short description of the issue."}
      </p>

      <p className="text-xs text-[#999] mt-12">
        {locale === "vi"
          ? "Cập nhật lần cuối: 30 tháng 4, 2026."
          : "Last updated: April 30, 2026."}
      </p>
    </div>
  );
}
